import React, { useState } from 'react';
import config from 'config';
import nextDynamic from 'next/dynamic';

import { ErrorBoundary } from '@/components/Error';
import { useAppConfig } from '@/hooks/useAppConfig';
import useNavigation from '@/hooks/useNavigation';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';
import dynamic from '@/utils/dynamic';

import { HELP_MENU_BLACKLISTED_ROUTES } from './consts';
import FeatureNudgeContextProvider from './context';
import { FeatureNudgeType } from './types';

const FloatingFeatureNudge = nextDynamic(() => import('./components/FloatingFeatureNudge'), {
  ssr: false,
});

const LiveChat = dynamic(
  () => import(/* webpackChunkName: "live-chat" */ '@/components/HelpMenu/components/LiveChat'),
);

const HelpMenuProvider = ({ children }: React.PropsWithChildren) => {
  const [featureNudge, setFeatureNudge] = useState<FeatureNudgeType | undefined>(undefined);

  const { pathname } = useNavigation();
  const { site: siteConfig } = useAppConfig();
  const user = useUserContext();
  const isAdmin = useAdminRoute();

  const isRouteBlacklisted = HELP_MENU_BLACKLISTED_ROUTES.has(pathname);

  const floatingFeatureNudge = React.useMemo(() => {
    if (
      isAdmin ||
      !featureNudge?.nudgeType ||
      user.meta?.completedOnboardingNudges?.includes(featureNudge.nudgeType)
    ) {
      return null;
    }
    return featureNudge;
  }, [featureNudge, isAdmin, user.meta?.completedOnboardingNudges]);

  return (
    <FeatureNudgeContextProvider setFeatureNudge={setFeatureNudge}>
      {children}
      {/* todo: remove this after dev work is done */}
      {(!isRouteBlacklisted && config.env === 'production') || true ? (
        <ErrorBoundary>
          {siteConfig.liveChat ? (
            <LiveChat config={siteConfig.liveChat} featureNudge={featureNudge} user={user} />
          ) : null}
        </ErrorBoundary>
      ) : null}
      {floatingFeatureNudge ? <FloatingFeatureNudge featureNudge={floatingFeatureNudge} /> : null}
    </FeatureNudgeContextProvider>
  );
};

export default HelpMenuProvider;
